/**
 * Décompte des sources facturées servi par l'API (DEV-621, écran M1).
 *
 * INTENTION. La carte d'abonnement dit au client d'où vient son nombre :
 * le forfait, la période en cours, les sources facturées et ce qui les
 * compose, la date à laquelle des places se libèrent et les places qu'il peut
 * libérer tout de suite. L'écran ne recalcule rien : il lit le bloc `plafond`
 * de `GET /company/billing/source/count`. Tant que ce bloc n'est pas servi,
 * la carte affiche le seul nombre facturé servi (`total`), sans inventer de
 * composition ; sans aucun nombre servi, elle n'en écrit aucun (une absence
 * n'est jamais un 0).
 *
 * Règles de rédaction : « facturées » (jamais « comptées », « possédez »),
 * « nous », ton formel, dates en français, aucun détail technique.
 */
import { inject, ref, watch } from "vue"
import { formatDate, resolveSubscriptionPlan } from "@core/helpers"
import { useAuthStore } from "@core/stores/auth.store"
import type { StripeSubscription } from "@core/types/auth"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import type { Services } from "@services"
import { dernierComptage } from "@core/composables/plafond-rafraichissement"

// ================================
// TYPES
// ================================

/**
 * Composition servie : les cinq feuilles de l'arbre du décompte (décision du
 * 14/10). Facturées = somme des quatre premières.
 */
export type CompositionDecompte = {
    /** Sur le compte, ajoutée pendant la période : facturée */
    sur_le_compte_ajoutees_dans_la_periode: number
    /** Sur le compte, ajoutée avant la période : facturée */
    sur_le_compte_ajoutees_avant: number
    /** Supprimée pendant la période, ajoutée pendant la période : facturée jusqu'à la fin */
    supprimees_ajoutees_dans_la_periode: number
    /** Supprimée, ajoutée avant, mise à jour pendant la période : facturée jusqu'à la fin */
    supprimees_apres_mise_a_jour: number
    /** Supprimée, ajoutée avant, sans mise à jour : non facturée, place libérée */
    supprimees_liberees: number
    /**
     * Sous-feuille de `sur_le_compte_ajoutees_avant` (recette du 15/10) :
     * mises à jour pendant la période, elles resteraient facturées jusqu'à la
     * fin si on les supprimait. Servie par la route de comptage seulement ;
     * `null` (ou absente) quand elle n'est pas mesurée : jamais 0.
     */
    sur_le_compte_ajoutees_avant_mises_a_jour?: number | null
    /**
     * Sous-feuille de `sur_le_compte_ajoutees_avant` : ni ajoutées ni mises à
     * jour pendant la période, elles libéreraient leur place dès la
     * suppression. Même lecture que la précédente.
     */
    sur_le_compte_ajoutees_avant_liberables?: number | null
}

/** Offre dont la règle s'applique : l'arbre complet (EA) ou la seule première question (EM) */
export type OffreDecompte = "EA" | "EM"

/** Bloc `plafond` servi (contrat DEV-2254), tel que lu par la carte */
export type PlafondDecompte = {
    forfait: number
    facturees: number
    /** Offre dont la règle s'applique, servie par l'API ; `null` si absente */
    offre: OffreDecompte | null
    /** Date ISO de début de la période en cours, servie ; `null` si absente */
    periode_debut: string | null
    /** Date ISO de fin de la période en cours */
    periode_fin: string | null
    blocage: boolean
    blocage_a_partir_du: string | null
    places_disponibles: number | null
    /** `null` si le bloc ne porte pas de composition lisible */
    composition: CompositionDecompte | null
    /**
     * Places libérées dès la suppression = sous-feuille
     * `sur_le_compte_ajoutees_avant_liberables` (décision du 15/10) ; `null`
     * quand elle n'est pas mesurée (jamais 0 à la place)
     */
    places_liberables: number | null
}

// ================================
// LECTURE DU BLOC SERVI
// ================================

const estNombre = (x: unknown): x is number =>
    typeof x === "number" && Number.isFinite(x) && x >= 0
const dateOuNull = (x: unknown): string | null =>
    typeof x === "string" && x !== ""
        ? x
        : x instanceof Date
          ? x.toISOString()
          : null

const CHAMPS_COMPOSITION = [
    "sur_le_compte_ajoutees_dans_la_periode",
    "sur_le_compte_ajoutees_avant",
    "supprimees_ajoutees_dans_la_periode",
    "supprimees_apres_mise_a_jour",
    "supprimees_liberees",
] as const

/**
 * Lit la composition servie ; `null` si une des cinq feuilles manque
 * (la carte montre alors les totaux sans l'arbre). Les deux sous-feuilles des
 * sources ajoutées avant la période ne sont retenues que servies toutes deux
 * et de somme égale à `sur_le_compte_ajoutees_avant` (sinon : carte d'avant).
 * @param brut - `plafond.composition` brut
 */
function lireComposition(brut: unknown): CompositionDecompte | null {
    if (!brut || typeof brut !== "object") return null
    const c = brut as Record<string, unknown>
    if (!CHAMPS_COMPOSITION.every((k) => estNombre(c[k]))) return null
    const lue = Object.fromEntries(
        CHAMPS_COMPOSITION.map((k) => [k, c[k] as number])
    ) as CompositionDecompte
    const majs = c.sur_le_compte_ajoutees_avant_mises_a_jour
    const libs = c.sur_le_compte_ajoutees_avant_liberables
    if (
        estNombre(majs) &&
        estNombre(libs) &&
        majs + libs === lue.sur_le_compte_ajoutees_avant
    ) {
        lue.sur_le_compte_ajoutees_avant_mises_a_jour = majs
        lue.sur_le_compte_ajoutees_avant_liberables = libs
    }
    return lue
}

/** Sources sur le compte selon ce que ferait leur suppression */
export type SurLeCompteSiSupprimees = {
    /** Ajoutées pendant la période : resteraient facturées jusqu'à la fin */
    ajoutees: number
    /** Ajoutées avant, mises à jour pendant la période : resteraient facturées */
    misesAJour: number
    /** Ni ajoutées ni mises à jour pendant la période : libéreraient leur place */
    liberables: number
}

/**
 * Décomposition des sources sur le compte selon ce que ferait leur
 * suppression ; `null` sans les deux sous-feuilles servies et cohérentes.
 */
export function surLeCompteSiSupprimees(
    c: CompositionDecompte
): SurLeCompteSiSupprimees | null {
    const misesAJour = c.sur_le_compte_ajoutees_avant_mises_a_jour
    const liberables = c.sur_le_compte_ajoutees_avant_liberables
    if (
        misesAJour === undefined ||
        misesAJour === null ||
        liberables === undefined ||
        liberables === null
    ) {
        return null
    }
    return {
        ajoutees: c.sur_le_compte_ajoutees_dans_la_periode,
        misesAJour,
        liberables,
    }
}

/** Sources sur le compte (deux premières feuilles) */
export const surLeCompte = (c: CompositionDecompte): number =>
    c.sur_le_compte_ajoutees_dans_la_periode + c.sur_le_compte_ajoutees_avant

/** Sources supprimées encore facturées jusqu'à la fin de la période */
export const supprimeesFacturees = (c: CompositionDecompte): number =>
    c.supprimees_ajoutees_dans_la_periode + c.supprimees_apres_mise_a_jour

/**
 * Composition affichable : servie ET cohérente avec le total servi (somme des
 * quatre feuilles facturées = `facturees`). Sinon `null` : la carte montre les
 * totaux seuls, sans arbre ni phrase de composition, et sans message.
 */
export function compositionCoherente(
    plafond: PlafondDecompte | null
): CompositionDecompte | null {
    const c = plafond?.composition
    if (!plafond || !c) return null
    return surLeCompte(c) + supprimeesFacturees(c) === plafond.facturees
        ? c
        : null
}

/**
 * Lit le bloc `plafond` servi. `null` s'il est absent ou mal formé : la carte
 * se replie alors sur le nombre facturé servi, sans composition.
 * @param brut - Valeur brute du champ `plafond`
 */
export function lirePlafondDecompte(brut: unknown): PlafondDecompte | null {
    if (!brut || typeof brut !== "object") return null
    const p = brut as Record<string, unknown>
    if (!estNombre(p.forfait) || !estNombre(p.facturees)) return null
    return {
        forfait: p.forfait,
        facturees: p.facturees,
        offre: p.offre === "EA" || p.offre === "EM" ? p.offre : null,
        periode_debut: dateOuNull(p.periode_debut),
        periode_fin: dateOuNull(p.periode_fin),
        blocage: p.blocage === true,
        blocage_a_partir_du: dateOuNull(p.blocage_a_partir_du),
        places_disponibles: estNombre(p.places_disponibles)
            ? p.places_disponibles
            : null,
        composition: lireComposition(p.composition),
        places_liberables: estNombre(p.places_liberables)
            ? p.places_liberables
            : null,
    }
}

// ================================
// TEXTES
// ================================

/** Date en JJ/MM/AAAA, heure de Paris ; `null` si absente ou illisible */
export function dateCourte(
    iso: string | Date | null | undefined
): string | null {
    return formatDate(iso, "DD/MM/YYYY", "") || null
}

/** « 1 source » / « 12 sources » */
const sources = (n: number): string => (n > 1 ? `${n} sources` : `${n} source`)

/**
 * « Forfait de 100 sources. » La période est toujours mensuelle : un forfait
 * annuel est jugé au mois, sans « par mois » ni « par an ».
 */
export function texteForfait(forfait: number): string {
    return `Forfait de ${sources(forfait)}.`
}

/**
 * « Période du 22/09 au 22/10. » à partir des seules bornes servies ; sans
 * début servi, « Période en cours jusqu'au 22/10. » ; sans fin, `null`.
 */
export function textePeriode(
    debut: string | Date | null | undefined,
    fin: string | Date | null | undefined
): string | null {
    const d = formatDate(debut, "DD/MM", "")
    const f = formatDate(fin, "DD/MM", "")
    if (d && f) return `Période du ${d} au ${f}.`
    if (f) return `Période en cours jusqu'au ${f}.`
    return null
}

/** Texte quand aucun nombre facturé n'est servi */
export const TEXTE_FACTUREES_INDISPONIBLE =
    "Nombre de sources facturées indisponible pour le moment."

/**
 * « 117 sources facturées : 92 sur votre compte, 25 supprimées pendant la
 * période, facturées jusqu'au 22/10. » Les parts nulles sont omises.
 * Sans composition servie : « 114 sources facturées sur la période en cours. »
 */
export function texteFacturees(
    facturees: number,
    composition: CompositionDecompte | null,
    fin?: string | Date | null
): string {
    const tete =
        facturees > 1
            ? `${facturees} sources facturées`
            : `${facturees} source facturée`
    if (!composition) return `${tete} sur la période en cours.`
    const parts: string[] = []
    const r = surLeCompte(composition)
    if (r > 0) parts.push(`${r} sur votre compte`)
    const s = supprimeesFacturees(composition)
    const jj = formatDate(fin, "DD/MM", "")
    if (s > 0) {
        const jusqua = jj
            ? `, ${s > 1 ? "facturées" : "facturée"} jusqu'au ${jj}`
            : ""
        parts.push(
            `${s} ${s > 1 ? "supprimées" : "supprimée"} pendant la période${jusqua}`
        )
    }
    return parts.length ? `${tete} : ${parts.join(", ")}.` : `${tete}.`
}

/**
 * Écart au forfait : « Dépassement de 16 sources. », « 8 places libres. »,
 * « Aucune place libre. »
 */
export function texteEcart(forfait: number, facturees: number): string {
    if (facturees > forfait) {
        return `Dépassement de ${sources(facturees - forfait)}.`
    }
    const libres = forfait - facturees
    if (libres === 0) return "Aucune place libre."
    return libres > 1 ? `${libres} places libres.` : "1 place libre."
}

/**
 * Date à laquelle les places des sources supprimées se libèrent : la fin de la
 * période, date à laquelle l'API rend de nouveau l'ajout possible
 * (`prochain_ajout_possible_le` = fin de période). `null` si aucune source
 * supprimée n'est facturée, sans composition cohérente ou sans date de fin.
 */
export function texteLiberation(plafond: PlafondDecompte): string | null {
    const c = compositionCoherente(plafond)
    if (!c) return null
    const n = supprimeesFacturees(c)
    const fin = formatDate(plafond.periode_fin, "DD/MM", "")
    if (n === 0 || !fin) return null
    return n > 1
        ? `${n} places se libèrent à la fin de la période, le ${fin}.`
        : `1 place se libère à la fin de la période, le ${fin}.`
}

/**
 * Places libérables tout de suite (Energy Analytics) ; `null` s'il n'y en a pas.
 */
export function texteLiberables(n: number | null): string | null {
    if (n === null || !(n > 0)) return null
    return n > 1
        ? `${n} places peuvent être libérées tout de suite : ce sont des sources qui n'ont été ni ajoutées ni mises à jour pendant la période, leur place se libère dès leur suppression.`
        : "1 place peut être libérée tout de suite : c'est une source qui n'a été ni ajoutée ni mise à jour pendant la période, sa place se libère dès sa suppression."
}

/**
 * Sources sur le compte décomposées selon ce que ferait leur suppression
 * (recette du 15/10) : « 92 sur votre compte : 10 ajoutées pendant la période et
 * 30 mises à jour pendant la période resteraient facturées jusqu'au 22/10 si vous les
 * supprimiez ; 52 libéreraient leur place dès la suppression. » Les parts
 * nulles sont omises. `null` sans les sous-feuilles servies (la carte garde
 * ses phrases d'avant) ou sans aucune source sur le compte.
 * @param c - composition servie et cohérente
 * @param fin - fin de la période servie
 */
export function texteSurLeCompteSiSupprimees(
    c: CompositionDecompte,
    fin?: string | Date | null
): string | null {
    const d = surLeCompteSiSupprimees(c)
    if (!d) return null
    const total = surLeCompte(c)
    if (total === 0) return null
    const parts: string[] = []
    const restent: string[] = []
    if (d.ajoutees > 0) {
        restent.push(
            `${d.ajoutees} ${d.ajoutees > 1 ? "ajoutées" : "ajoutée"} pendant la période`
        )
    }
    if (d.misesAJour > 0) {
        restent.push(
            `${d.misesAJour} ${d.misesAJour > 1 ? "mises" : "mise"} à jour pendant la période`
        )
    }
    const nRestent = d.ajoutees + d.misesAJour
    if (nRestent > 0) {
        const jj = formatDate(fin, "DD/MM", "")
        const jusqua = jj ? `jusqu'au ${jj}` : "jusqu'à la fin de la période"
        parts.push(
            nRestent > 1
                ? `${restent.join(" et ")} resteraient facturées ${jusqua} si vous les supprimiez`
                : `${restent.join(" et ")} resterait facturée ${jusqua} si vous la supprimiez`
        )
    }
    if (d.liberables > 0) {
        parts.push(
            d.liberables > 1
                ? `${d.liberables} libéreraient leur place dès la suppression`
                : "1 libérerait sa place dès la suppression"
        )
    }
    return `${total} sur votre compte : ${parts.join(" ; ")}.`
}

// ================================
// DEVENIR DE LA PLACE (maquette v2 du 16/10, « barre et tableau reliés »)
// ================================

/**
 * Ce que devient la place d'une source : la barre, le tableau du décompte et
 * la liste des sources la disent avec la même couleur.
 * - `restent` : sur le compte, resteraient facturées jusqu'à la fin de la
 *   période si on les supprimait ;
 * - `liberent` : sur le compte, libèrent une place dès la suppression ;
 * - `supprimees_facturees` : supprimées, encore facturées jusqu'à la fin ;
 * - `non_facturee` : supprimées, non facturées ;
 * - `excedent` : au-delà du forfait ;
 * - `reste` : places restantes (partie vide de la barre).
 */
export type DevenirPlace =
    | "restent"
    | "liberent"
    | "supprimees_facturees"
    | "non_facturee"
    | "excedent"
    | "reste"

/** Définition d'un devenir : couleur de barre et étiquette */
export type DefinitionDevenir = {
    cle: DevenirPlace
    /** Libellé court du devenir (aide à la lecture des mini-barres) */
    libelle: string
    /** Couleur du segment et de la mini-barre (variable du thème) */
    couleur: string
    /** Liseré du point de légende (couleur pâle), `null` sans lisere */
    lisere: string | null
    /** Gravité du BaseTag de l'étiquette ; `undefined` : étiquette primaire */
    severite: "primary" | "success" | "secondary" | "danger" | undefined
    /** Classe de l'étiquette quand la gravité PrimeVue n'existe pas */
    classeEtiquette: string | undefined
}

/**
 * Référentiel unique des devenirs de la place : une couleur par devenir,
 * partout (barre, mini-barres, étiquettes « Sa place se libère » et « Si vous
 * la supprimez »). Aucun orange : le bleu clair dit « supprimée, encore
 * facturée », le neutre « non facturée ».
 */
export const DEVENIRS_PLACE: Record<DevenirPlace, DefinitionDevenir> = {
    restent: {
        cle: "restent",
        libelle: "Resteraient facturées jusqu'à la fin de la période",
        couleur: "var(--p-primary-500)",
        lisere: null,
        severite: "primary",
        classeEtiquette: undefined,
    },
    liberent: {
        cle: "liberent",
        libelle: "Libèrent une place dès la suppression",
        couleur: "var(--p-green-500)",
        lisere: null,
        severite: "success",
        classeEtiquette: undefined,
    },
    supprimees_facturees: {
        cle: "supprimees_facturees",
        libelle: "Supprimées, encore facturées jusqu'à la fin de la période",
        couleur: "var(--p-primary-100)",
        lisere: "var(--p-primary-500)",
        severite: undefined,
        classeEtiquette: "bg-[#eef2fb]! text-primary-600!",
    },
    non_facturee: {
        cle: "non_facturee",
        libelle: "Non facturées",
        couleur: "var(--p-surface-300)",
        lisere: null,
        severite: "secondary",
        classeEtiquette: "bg-surface-100! text-surface-700!",
    },
    excedent: {
        cle: "excedent",
        libelle: "Excédent",
        couleur: "var(--p-red-500)",
        lisere: null,
        severite: "danger",
        classeEtiquette: undefined,
    },
    reste: {
        cle: "reste",
        libelle: "Places restantes",
        couleur: "var(--p-surface-200)",
        lisere: "var(--p-surface-300)",
        severite: undefined,
        classeEtiquette: undefined,
    },
}

// ================================
// ARBRE DU DÉCOMPTE (décision du 14/10, tableau de synthèse du 15/10)
// ================================

/** Ton d'une feuille : facturée, facturée jusqu'à la fin, non facturée */
export type TonFeuille = "oui" | "jusqua" | "non"

/** Ton de la colonne « Sa place se libère » : tout de suite, ou à la fin */
export type TonPlace = "libre" | "fin"

/** Filtres de la liste de facturation posés par une ligne (paramètres de requête) */
export type FiltresLigne = Record<string, string>

/**
 * Ligne du tableau : les cellules fusionnées portent leur `rowspan`, les
 * autres sont omises. Une source ajoutée pendant la période est mise à jour
 * par construction : sa question 2 s'étend sur la colonne 3 (`colspan: 2`).
 */
export type LigneArbre = {
    /** Question 1, présente sur la première ligne de son groupe */
    q1?: { texte: string; rowspan: number }
    /**
     * Question 2, présente sur la première ligne de son groupe ; `colspan` 2
     * quand elle absorbe la question 3 ; `total` : nombre du groupe fusionné
     * quand ses sous-lignes ne sont pas mesurées (le total reste lisible)
     */
    q2?: { texte: string; rowspan: number; colspan?: number; total?: number }
    /** Question 3 (Energy Analytics), absente quand la question 2 l'absorbe */
    q3?: { texte: string }
    /** « Facturée sur la période ? » */
    feuille: string
    ton: TonFeuille
    /** « Sa place se libère » */
    place: string
    tonPlace: TonPlace
    /** Devenir de la place : couleur de l'étiquette, de la mini-barre et du survol */
    devenir: DevenirPlace
    /** Nombre de sources ; `null` : non mesuré (affiché « non mesuré », jamais 0) */
    sources: number | null
    /**
     * Filtres de la liste de facturation qui retrouvent ces sources (le nombre
     * est un lien) ; `null` : la liste ne les sert pas (sources supprimées non
     * facturées), le nombre n'est pas un lien
     */
    filtres: FiltresLigne | null
}

/** Texte d'un nombre de l'arbre non mesuré */
export const NON_MESURE_ARBRE = "non mesuré"

/** Nombre affiché d'une ligne de l'arbre */
export const sourcesArbre = (n: number | null): string =>
    n === null ? NON_MESURE_ARBRE : String(n)

/** En-têtes de l'arbre, dans l'ordre */
export const QUESTIONS_ARBRE = {
    q1: "1. Sur votre compte aujourd'hui ?",
    q2: "2. Ajoutée pendant la période ?",
    q3: "3. Mise à jour pendant la période ?",
    feuille: "Facturée sur la période ?",
    place: "Sa place se libère",
    sources: "Sources",
} as const

/** Feuilles de la liste M2 (« Facturée sur la période ? » et son motif) */
export const FEUILLES = {
    oui: "Oui",
    jusqua: (fin: string | null) =>
        fin ? `Oui, jusqu'au ${fin}` : "Oui, jusqu'à la fin de la période",
    non: "Non, place libérée dès la suppression",
} as const

/** Réponses du tableau de synthèse (demande de Mathieu du 15/10) */
export const REPONSES_ARBRE = {
    facturee: {
        oui: "Oui",
        jusqua: (fin: string | null) =>
            fin ? `Oui, jusqu'au ${fin}` : "Oui, jusqu'à la fin de la période",
        non: "Non",
    },
    place: {
        siSupprimee: (fin: string | null) =>
            fin
                ? `le ${fin}, si vous la supprimez`
                : "à la fin de la période, si vous la supprimez",
        desSuppression: "dès la suppression",
        fin: (fin: string | null) =>
            fin ? `le ${fin}` : "à la fin de la période",
        liberee: "libérée dès la suppression",
    },
} as const

/**
 * Lignes du tableau de synthèse à partir de la composition servie par la
 * route de comptage : trois questions oui/non (huit combinaisons), six lignes
 * en Energy Analytics, car une source ajoutée pendant la période est mise à
 * jour par construction ; deux questions, quatre lignes en Energy Management
 * (la mise à jour n'y compte jamais). Chaque ligne dit si les sources sont
 * facturées sur la période et quand leur place se libère, et porte les
 * filtres de la liste de facturation qui les retrouvent.
 */
export function lignesArbre(
    c: CompositionDecompte,
    offre: OffreDecompte,
    debut: string | Date | null | undefined,
    fin: string | Date | null | undefined
): LigneArbre[] {
    const d = formatDate(debut, "DD/MM", "")
    const f = formatDate(fin, "DD/MM", "") || null
    const pendant = d ? `Oui, depuis le ${d}` : "Oui"
    const avant = d ? `Non, avant le ${d}` : "Non, avant la période"
    const R = REPONSES_ARBRE
    const supprimee = "Non, supprimée pendant la période"
    if (offre === "EM") {
        return [
            {
                q1: { texte: "Oui", rowspan: 2 },
                q2: { texte: pendant, rowspan: 1 },
                feuille: R.facturee.oui,
                ton: "oui",
                place: R.place.siSupprimee(f),
                tonPlace: "fin",
                devenir: "restent",
                sources: c.sur_le_compte_ajoutees_dans_la_periode,
                filtres: { sur_le_compte: "oui", ajoutee_periode: "oui" },
            },
            {
                q2: { texte: avant, rowspan: 1 },
                feuille: R.facturee.oui,
                ton: "oui",
                place: R.place.desSuppression,
                tonPlace: "libre",
                devenir: "liberent",
                sources: c.sur_le_compte_ajoutees_avant,
                filtres: { sur_le_compte: "oui", ajoutee_periode: "non" },
            },
            {
                q1: { texte: supprimee, rowspan: 2 },
                q2: { texte: pendant, rowspan: 1 },
                feuille: R.facturee.jusqua(f),
                ton: "jusqua",
                place: R.place.fin(f),
                tonPlace: "fin",
                devenir: "supprimees_facturees",
                sources: c.supprimees_ajoutees_dans_la_periode,
                filtres: { sur_le_compte: "non", ajoutee_periode: "oui" },
            },
            {
                q2: { texte: avant, rowspan: 1 },
                feuille: R.facturee.non,
                ton: "non",
                place: R.place.liberee,
                tonPlace: "libre",
                devenir: "non_facturee",
                sources: c.supprimees_liberees,
                filtres: null,
            },
        ]
    }
    // Sous-feuilles servies par la route de comptage ; non mesurées, chaque
    // sous-ligne dit « non mesuré » et la question 2 fusionnée garde le total.
    const parSort = surLeCompteSiSupprimees(c)
    // une source ajoutée pendant la période est facturée sans condition de
    // mise à jour : la question 3 est absorbée
    const absorbe = `${pendant} : facturée même sans mise à jour`
    return [
        {
            q1: { texte: "Oui", rowspan: 3 },
            q2: { texte: absorbe, rowspan: 1, colspan: 2 },
            feuille: R.facturee.oui,
            ton: "oui",
            place: R.place.siSupprimee(f),
            tonPlace: "fin",
            devenir: "restent",
            sources: c.sur_le_compte_ajoutees_dans_la_periode,
            filtres: { sur_le_compte: "oui", ajoutee_periode: "oui" },
        },
        {
            q2: {
                texte: avant,
                rowspan: 2,
                ...(parSort ? {} : { total: c.sur_le_compte_ajoutees_avant }),
            },
            q3: { texte: "Oui" },
            feuille: R.facturee.oui,
            ton: "oui",
            place: R.place.siSupprimee(f),
            tonPlace: "fin",
            devenir: "restent",
            sources: parSort ? parSort.misesAJour : null,
            filtres: {
                sur_le_compte: "oui",
                ajoutee_periode: "non",
                maj_periode: "oui",
            },
        },
        {
            q3: { texte: "Non" },
            feuille: R.facturee.oui,
            ton: "oui",
            place: R.place.desSuppression,
            tonPlace: "libre",
            devenir: "liberent",
            sources: parSort ? parSort.liberables : null,
            filtres: {
                sur_le_compte: "oui",
                ajoutee_periode: "non",
                maj_periode: "non",
            },
        },
        {
            q1: { texte: supprimee, rowspan: 3 },
            q2: { texte: absorbe, rowspan: 1, colspan: 2 },
            feuille: R.facturee.jusqua(f),
            ton: "jusqua",
            place: R.place.fin(f),
            tonPlace: "fin",
            devenir: "supprimees_facturees",
            sources: c.supprimees_ajoutees_dans_la_periode,
            filtres: { sur_le_compte: "non", ajoutee_periode: "oui" },
        },
        {
            q2: { texte: avant, rowspan: 2 },
            q3: { texte: "Oui" },
            feuille: R.facturee.jusqua(f),
            ton: "jusqua",
            place: R.place.fin(f),
            tonPlace: "fin",
            devenir: "supprimees_facturees",
            sources: c.supprimees_apres_mise_a_jour,
            filtres: {
                sur_le_compte: "non",
                ajoutee_periode: "non",
                maj_periode: "oui",
            },
        },
        {
            q3: { texte: "Non" },
            feuille: R.facturee.non,
            ton: "non",
            place: R.place.liberee,
            tonPlace: "libre",
            devenir: "non_facturee",
            sources: c.supprimees_liberees,
            // la liste ne sert pas les sources supprimées non facturées
            // (`filtreServiEA`) : aucun lien, le nombre ne s'y retrouverait pas
            filtres: null,
        },
    ]
}

// ================================
// BARRE DE SYNTHÈSE DU FORFAIT (demande de Mathieu du 16/10)
// ================================

/** Segment de la barre de synthèse, et sa ligne de légende */
export type SegmentBarre = {
    id:
        | "restent"
        | "liberent"
        | "sur_le_compte"
        | "supprimees"
        | "reste"
        | "excedent"
    libelle: string
    /** Nombre de sources (ou de places pour « reste » et « excédent ») */
    nombre: number
    /**
     * Étendue dessinée dans la barre : le nombre, sauf en dépassement, où
     * les sources remplissent le forfait dans l'ordre des segments et
     * l'excédent dessine le reste (la légende garde les vrais nombres)
     */
    etendue: number
    /** Couleur du segment (variable du thème), celle de son devenir */
    couleur: string
    /** Devenir de la place : relie le segment aux lignes du tableau */
    devenir: DevenirPlace
    /** Liseré du point de légende, `null` sans liseré */
    lisere: string | null
    /** Vrai : partie vide de la barre (places restantes), pas un segment */
    vide: boolean
    /** Filtres de la page des sources qui retrouvent ces sources ; `null` : pas de lien */
    filtres: FiltresLigne | null
}

/** Barre de synthèse : bornée au forfait, ou aux facturées si elles le dépassent */
export type BarreDecompte = {
    max: number
    segments: SegmentBarre[]
    /** Vrai : les sources sur le compte ne sont pas réparties (sous-feuilles non mesurées) */
    repartitionNonMesuree: boolean
}

/** Couleurs des segments, lues du référentiel des devenirs */
export const COULEURS_BARRE = {
    restent: DEVENIRS_PLACE.restent.couleur,
    liberent: DEVENIRS_PLACE.liberent.couleur,
    supprimees: DEVENIRS_PLACE.supprimees_facturees.couleur,
    excedent: DEVENIRS_PLACE.excedent.couleur,
    reste: DEVENIRS_PLACE.reste.couleur,
} as const

/**
 * Segments de la barre de synthèse, en nombre de sources, lus de la seule
 * composition servie et cohérente :
 * 1. sur le compte, resteraient facturées si on les supprimait (EA : ajoutées
 *    pendant la période et ajoutées avant mises à jour ; EM : ajoutées
 *    pendant la période) ;
 * 2. sur le compte, libèrent une place dès la suppression (EA :
 *    `sur_le_compte_ajoutees_avant_liberables` ; EM : ajoutées avant) ; en
 *    EA non mesurée, un seul segment « Sur votre compte » (jamais 0) ;
 * 3. supprimées pendant la période, facturées jusqu'à la fin ;
 * 4. le reste : places restantes (partie vide) ou excédent ; en
 *    dépassement, les sources remplissent le forfait dans cet ordre et
 *    l'excédent dessine la part au-delà (la légende garde les vrais nombres).
 * Les segments à 0 sont omis, sauf les places restantes. `null` si la
 * barre n'a aucune étendue (forfait et facturées nuls).
 */
export function barreDecompte(
    c: CompositionDecompte,
    offre: OffreDecompte,
    forfait: number,
    facturees: number,
    fin?: string | Date | null
): BarreDecompte | null {
    const max = Math.max(forfait, facturees)
    if (!(max > 0)) return null
    const f = formatDate(fin, "DD/MM", "")
    const segments: SegmentBarre[] = []
    // place encore dessinable dans le forfait, consommée dans l'ordre
    let dansLeForfait = forfait
    const ajouter = (
        s: Omit<SegmentBarre, "vide" | "etendue" | "couleur" | "lisere">
    ) => {
        if (!(s.nombre > 0)) return
        let etendue = s.nombre
        if (s.id !== "excedent" && facturees > forfait) {
            etendue = Math.min(s.nombre, dansLeForfait)
            dansLeForfait -= etendue
        }
        const d = DEVENIRS_PLACE[s.devenir]
        segments.push({
            ...s,
            etendue,
            couleur: d.couleur,
            lisere: d.lisere,
            vide: false,
        })
    }
    let repartitionNonMesuree = false
    let restent: number | null
    let liberent: number | null
    if (offre === "EM") {
        restent = c.sur_le_compte_ajoutees_dans_la_periode
        liberent = c.sur_le_compte_ajoutees_avant
    } else {
        const d = surLeCompteSiSupprimees(c)
        restent = d ? d.ajoutees + d.misesAJour : null
        liberent = d ? d.liberables : null
    }
    if (restent === null || liberent === null) {
        repartitionNonMesuree = true
        ajouter({
            id: "sur_le_compte",
            libelle: "Sur votre compte",
            nombre: surLeCompte(c),
            // répartition non mesurée : la couleur des sources sur le compte
            devenir: "restent",
            filtres: { sur_le_compte: "oui" },
        })
    } else {
        ajouter({
            id: "restent",
            libelle:
                restent > 1
                    ? "Sur votre compte, resteraient facturées si vous les supprimiez"
                    : "Sur votre compte, resterait facturée si vous la supprimiez",
            nombre: restent,
            devenir: "restent",
            filtres: { sur_le_compte: "oui", si_supprimee: "reste" },
        })
        ajouter({
            id: "liberent",
            libelle:
                liberent > 1
                    ? "Sur votre compte, libèrent une place dès la suppression"
                    : "Sur votre compte, libère une place dès la suppression",
            nombre: liberent,
            devenir: "liberent",
            filtres: { sur_le_compte: "oui", si_supprimee: "libere" },
        })
    }
    const supprimees = supprimeesFacturees(c)
    const jusqua = f ? `jusqu'au ${f}` : "jusqu'à la fin de la période"
    ajouter({
        id: "supprimees",
        libelle:
            supprimees > 1
                ? `Supprimées pendant la période, facturées ${jusqua}`
                : `Supprimée pendant la période, facturée ${jusqua}`,
        nombre: supprimees,
        devenir: "supprimees_facturees",
        filtres: { sur_le_compte: "non", facturee: "oui" },
    })
    if (facturees > forfait) {
        ajouter({
            id: "excedent",
            libelle: "Excédent",
            nombre: facturees - forfait,
            devenir: "excedent",
            filtres: null,
        })
    } else {
        segments.push({
            id: "reste",
            libelle: "Places restantes",
            nombre: forfait - facturees,
            etendue: forfait - facturees,
            couleur: DEVENIRS_PLACE.reste.couleur,
            devenir: "reste",
            lisere: DEVENIRS_PLACE.reste.lisere,
            vide: true,
            filtres: null,
        })
    }
    return { max, segments, repartitionNonMesuree }
}

/**
 * Segments dessinés de la barre (et de la mini-barre du pied du tableau) :
 * ni la partie vide des places restantes, ni un segment d'étendue nulle (en
 * dépassement, un segment que le forfait rempli ne laisse plus dessiner). La
 * légende, elle, garde tous les segments et leurs vrais nombres.
 */
export const segmentsDessines = (barre: BarreDecompte | null): SegmentBarre[] =>
    (barre?.segments ?? []).filter((s) => !s.vide && s.etendue > 0)

/**
 * Largeur d'une part sur l'échelle de la barre, en pourcentage exact (sans
 * l'arrondi de MeterGroup) ; « 0% » sans échelle
 * @param valeur - nombre ou étendue
 * @param max - échelle : forfait, ou facturées en dépassement
 */
export function largeurSurEchelle(valeur: number | null, max: number): string {
    if (!(max > 0) || valeur === null || !(valeur > 0)) return "0%"
    return `${(Math.min(valeur, max) / max) * 100}%`
}

/** Pied de l'arbre : « Facturées sur la période (forfait de 100) » */
export function piedArbre(forfait: number): string {
    return `Facturées sur la période (forfait de ${forfait})`
}

/** Note sous le tableau : ce que veut dire « mise à jour pendant la période » */
export const NOTE_ARBRE =
    "« Mise à jour pendant la période » : vous avez relancé la source, ou une donnée est arrivée pour elle pendant la période."

/**
 * Offre dont la règle s'applique : celle servie par l'API (`plafond.offre`),
 * sinon `resolveSubscriptionPlan`. `null` si aucune n'est connue : jamais de
 * repli silencieux sur Energy Management (l'arbre n'est alors pas montré).
 */
export function offreServie(
    plafond: PlafondDecompte | null,
    abonnements: StripeSubscription[] | null | undefined
): OffreDecompte | null {
    if (plafond?.offre) return plafond.offre
    const plan = resolveSubscriptionPlan(abonnements)
    return plan === "EA" || plan === "EM" ? plan : null
}

// ================================
// COMPOSABLE
// ================================

/**
 * Lit le décompte servi (`GET /company/billing/source/count`) : nombre facturé
 * et bloc `plafond`. Une réponse sans nombre ou une erreur laisse `total` et
 * `plafond` à `null` : l'écran n'écrit alors aucun nombre. `isLoaded` reste
 * faux tant que la première réponse n'est pas arrivée.
 *
 * Relecture : chaque fois que le plafond du store change (ajout de source,
 * `rafraichirPlafondServi`), et à la demande (`fetch`).
 */
export function useCompanyPlafond() {
    const services = inject<Services>("services")
    const authStore = useAuthStore()
    const plafond = ref<PlafondDecompte | null>(null)
    const total = ref<number | null>(null)
    const isLoaded = ref(false)

    /** Pose une réponse de la route de comptage */
    function appliquer(reponse: unknown) {
        const r = (reponse ?? {}) as { total?: unknown; plafond?: unknown }
        total.value = estNombre(r.total) ? r.total : null
        plafond.value = lirePlafondDecompte(r.plafond)
        isLoaded.value = true
    }
    // réponses déjà connues à la création : jamais reprises plus tard
    let comptageLu = dernierComptage.value?.numero ?? 0

    const { fetch, isFetching } = useFetchWrapper(
        async () => {
            if (!services?.$getBillingSourceCount) {
                total.value = null
                plafond.value = null
                isLoaded.value = true
                return
            }
            appliquer(await services.$getBillingSourceCount())
        },
        () => {
            total.value = null
            plafond.value = null
            isLoaded.value = true
        }
    )

    // Un ajout relit le plafond (`rafraichirPlafondServi`, qui pose sa réponse
    // dans `dernierComptage` puis dans le store) : la carte reprend cette
    // réponse sans relancer l'appel. Un autre changement du store (profil
    // relu, changement d'espace) relit le décompte complet.
    watch(
        () => authStore.companyPlafond,
        () => {
            if (!isLoaded.value) return
            const d = dernierComptage.value
            // réponse lue pour l'entreprise courante seulement (changement
            // d'espace : celle de l'ancienne entreprise n'est jamais reprise)
            if (
                d &&
                d.numero > comptageLu &&
                d.companyId === authStore.companyId
            ) {
                comptageLu = d.numero
                appliquer(d.reponse)
                return
            }
            void fetch(undefined, { silent: true })
        }
    )

    return { plafond, total, isLoaded, isFetching, fetch }
}
